import Stack from 'components/elements/Stack'
import { useCallback, useEffect, useState } from 'react'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import LibraryService from 'services/LibraryService'
import { FolderTypes, KnowFolder } from 'interfaces/KnowFolder'
import { toast } from 'react-toastify'
import _useTranslation from 'lib/hooks/_useTranslation'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { ApiError } from 'services/ApiService'
import { Know } from 'interfaces/Know'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import GrayFolder from 'assets/app/library/gray_folder.svg'
import { KnowCountBadge } from './Folder'
import { useRouter } from 'next/router'
import { appLibraryFolderRoute } from 'lib/constants/routes'
import { AnalyticsEventKnowSaveToFolder } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const FolderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    place-content: start;
    gap: 8px;
    height: 400px;
    overflow: auto;
    padding: 0 15px;
`

export const FolderCard = styled.div<{ isSelected?: boolean }>`
    width: 100%;
    height: 80px;
    position: relative;
    border-radius: ${theme.borderRadius.normal};
    display: grid;
    padding: 15px;
    place-content: center;
    border: 1px solid ${({ isSelected }) => (isSelected ? theme.colors.deepSeaBlue : theme.colors.transparent)};
    background-color: ${theme.colors.backgroundTransparent};
    grid-template-columns: auto 1fr;
    gap: 15px;
    cursor: pointer;
    .folder {
        position: relative;
        top: 25%;
        path {
            fill: ${theme.colors.backgroundTransparent} !important;
        }
    }
`

const Spacer = styled.div`
    height: 10px;
`

interface Props {
    open: boolean
    onClose: () => void
    know: Know
    isDarkTheme?: boolean
}

const AddKnowToFolderDialog = (props: Props) => {
    const [folders, setFolders] = useState<KnowFolder[]>([])
    const { t } = _useTranslation('app')
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const { t: tCommon } = _useTranslation('common')
    const router = useRouter()

    const fetchFolders = useCallback(() => {
        if (!authenticatedUser?.uuid) return

        LibraryService.listFolders(authenticatedUser.uuid, true)
            .then((folders) => {
                setFolders(folders.filter((folder) => folder.type !== FolderTypes.LikedKnow))
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'library.createFolder')
                } else {
                    throw error
                }
            })
    }, [])

    useEffect(() => {
        fetchFolders()
    }, [fetchFolders])

    const redirectToFolder = (folderUuid: string) => router.push(appLibraryFolderRoute(folderUuid))

    const addKnowToFolder = (folderUuid: string) => {
        LibraryService.addKnowToFolder(folderUuid, props.know.uuid)
            .then(() => {
                trackEvent(AnalyticsEventKnowSaveToFolder, { know: props.know })
                toast.success(
                    <Body1 onClick={() => redirectToFolder(folderUuid)} fontWeight={400}>
                        {t('app/addKnowToFolderDialogSuccessToastMessage')}
                    </Body1>
                )
                props.onClose()
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    if (error.type === ErrorTypes.NotFound) {
                        toast.error(t('app/folderNotFoundError'))
                    } else {
                        error.handleUnknownError(tCommon, 'library.addKnowToFolder')
                    }
                } else {
                    throw error
                }
            })
    }

    return (
        <FeatureDialog
            title={t('app/addKnowToFolderDialogTitle')}
            {...props}
            ariaPrefix="create-folder"
            maxWidth={600}
            allowVerticalOverflow
            isDarkTheme={props.isDarkTheme}
            backgroundColor={props.isDarkTheme ? theme.colors.webappDialogBackground : undefined}
            headerColor={props.isDarkTheme ? theme.colors.webappDialogBackground : undefined}
            fullNoPadding
        >
            <Stack gutter={25}>
                <Spacer />
                <Body2
                    fontFamily="var(--font-open-sans)"
                    color={theme.colors.grayScaleSecondary}
                    style={{ width: '100%', padding: '0 20px' }}
                >
                    {t('app/existingFoldersLabel')}
                </Body2>
                <FolderWrapper>
                    {folders.map((folder) => (
                        <FolderCard key={folder.uuid} onClick={() => addKnowToFolder(folder.uuid)}>
                            <GrayFolder className="folder" />
                            <Stack gutter={5}>
                                <Body1
                                    fontFamily="var(--font-open-sans)"
                                    fontWeight={700}
                                    color={theme.colors.grayScaleSecondary}
                                >
                                    {folder.name}
                                </Body1>
                                <KnowCountBadge count={folder.knowsCount} />
                            </Stack>
                        </FolderCard>
                    ))}
                </FolderWrapper>
                <Spacer />
            </Stack>
        </FeatureDialog>
    )
}

export default AddKnowToFolderDialog
